import * as THREE from 'three';
import { FOW_GLSL, fowUniforms } from './fowShader';

export type ParticleKind = 'smoke' | 'dust' | 'fire' | 'spark' | 'splash' | 'flash' | 'chips' | 'blood' | 'muzzle' | 'heal' | 'soot' | 'flame' | 'glow';

interface Spec {
  life: number;
  size: [number, number];
  color: [number, number, number];
  endColor: [number, number, number];
  alpha: number;
  gravity: number;
  drag: number;
  additive: boolean;
  grow: number;
}

const SPECS: Record<ParticleKind, Spec> = {
  smoke: { life: 3.5, size: [0.25, 0.45], color: [0.4, 0.38, 0.36], endColor: [0.24, 0.23, 0.22], alpha: 0.5, gravity: -0.35, drag: 0.6, additive: false, grow: 1.6 },
  dust: { life: 1.4, size: [0.2, 0.4], color: [0.62, 0.55, 0.42], endColor: [0.5, 0.45, 0.36], alpha: 0.5, gravity: -0.05, drag: 2.2, additive: false, grow: 1.4 },
  fire: { life: 0.9, size: [0.42, 0.66], color: [1.0, 0.8, 0.35], endColor: [0.9, 0.25, 0.05], alpha: 0.9, gravity: -1.4, drag: 1.2, additive: true, grow: -0.6 },
  // Solid flame core drawn under the glow, so fire still reads against bright ground and sky.
  flame: { life: 0.7, size: [0.32, 0.5], color: [1.0, 0.6, 0.1], endColor: [0.85, 0.15, 0.02], alpha: 0.95, gravity: -1.8, drag: 1.5, additive: false, grow: -0.7 },
  // Thick dark smoke from burning buildings.
  soot: { life: 4, size: [0.35, 0.6], color: [0.16, 0.15, 0.14], endColor: [0.1, 0.1, 0.1], alpha: 0.7, gravity: -0.55, drag: 0.6, additive: false, grow: 2 },
  spark: { life: 0.35, size: [0.05, 0.09], color: [1.0, 0.9, 0.6], endColor: [1.0, 0.5, 0.1], alpha: 1, gravity: 4, drag: 0.5, additive: true, grow: -0.5 },
  splash: { life: 0.7, size: [0.12, 0.2], color: [0.8, 0.9, 1.0], endColor: [0.7, 0.85, 0.95], alpha: 0.7, gravity: 6, drag: 0.4, additive: false, grow: 0.5 },
  flash: { life: 0.18, size: [0.9, 1.3], color: [1.0, 0.85, 0.5], endColor: [1.0, 0.5, 0.2], alpha: 1, gravity: 0, drag: 0, additive: true, grow: 0.8 },
  chips: { life: 0.9, size: [0.06, 0.1], color: [0.55, 0.4, 0.25], endColor: [0.45, 0.32, 0.2], alpha: 1, gravity: 7, drag: 0.3, additive: false, grow: 0 },
  blood: { life: 0.5, size: [0.06, 0.1], color: [0.55, 0.08, 0.06], endColor: [0.35, 0.05, 0.04], alpha: 0.9, gravity: 5, drag: 0.5, additive: false, grow: 0 },
  heal: { life: 1.1, size: [0.1, 0.16], color: [1.0, 0.92, 0.55], endColor: [0.7, 1.0, 0.6], alpha: 0.9, gravity: -0.9, drag: 1.5, additive: true, grow: -0.4 },
  // A wide, faint additive halo around flames: the light a fire throws, without a bloom pass.
  glow: { life: 0.55, size: [1.5, 2.1], color: [1.0, 0.55, 0.2], endColor: [0.7, 0.2, 0.04], alpha: 0.2, gravity: -0.5, drag: 1.5, additive: true, grow: 0.25 },
  muzzle: { life: 0.9, size: [0.15, 0.25], color: [0.85, 0.85, 0.85], endColor: [0.6, 0.6, 0.6], alpha: 0.6, gravity: -0.4, drag: 2.5, additive: false, grow: 1.8 },
};

class Pool {
  readonly points: THREE.Points;
  private pos: Float32Array;
  private col: Float32Array;
  private size: Float32Array;
  private vel: Float32Array;
  private age: Float32Array;
  private life: Float32Array;
  private kind: Uint8Array;
  private base: Float32Array;
  private next = 0;
  private alive = 0;
  constructor(readonly capacity: number, additive: boolean) {
    this.pos = new Float32Array(capacity * 3);
    this.col = new Float32Array(capacity * 4);
    this.size = new Float32Array(capacity);
    this.vel = new Float32Array(capacity * 3);
    this.age = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.kind = new Uint8Array(capacity);
    this.base = new Float32Array(capacity);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aColor', new THREE.BufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aSize', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    const m = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      uniforms: { uScale: { value: 600 }, uFowTex: fowUniforms.uFowTex, uFowSize: fowUniforms.uFowSize, uFowEnabled: fowUniforms.uFowEnabled },
      vertexShader: `
        attribute vec4 aColor;
        attribute float aSize;
        uniform float uScale;
        varying vec4 vColor;
        varying vec3 vWp;
        void main() {
          vColor = aColor;
          vWp = position;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * uScale / -mv.z;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        varying vec4 vColor;
        varying vec3 vWp;
        ${FOW_GLSL}
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float r = dot(d, d) * 4.0;
          if (r > 1.0) discard;
          float a = vColor.a * (1.0 - r) * (1.0 - r);
          float f = fowLight(vWp);
          if (f < 0.5) discard;
          gl_FragColor = vec4(vColor.rgb, a);
        }`,
    });
    this.points = new THREE.Points(g, m);
    this.points.frustumCulled = false;
    this.points.renderOrder = 5;
  }

  get uniforms() {
    return (this.points.material as THREE.ShaderMaterial).uniforms;
  }

  emit(kind: ParticleKind, k: number, x: number, y: number, z: number, vx: number, vy: number, vz: number): void {
    const s = SPECS[kind];
    const i = this.next;
    this.next = (this.next + 1) % this.capacity;
    if (this.life[i] <= this.age[i]) this.alive++;
    this.pos[i * 3] = x;
    this.pos[i * 3 + 1] = y;
    this.pos[i * 3 + 2] = z;
    this.vel[i * 3] = vx;
    this.vel[i * 3 + 1] = vy;
    this.vel[i * 3 + 2] = vz;
    this.age[i] = 0;
    this.life[i] = s.life * (0.7 + Math.random() * 0.6);
    this.kind[i] = k;
    this.base[i] = s.size[0] + Math.random() * (s.size[1] - s.size[0]);
  }

  update(dt: number): void {
    const kinds = KIND_LIST;
    for (let i = 0; i < this.capacity; i++) {
      if (this.age[i] >= this.life[i]) {
        this.size[i] = 0;
        continue;
      }
      const s = SPECS[kinds[this.kind[i]]];
      this.age[i] += dt;
      const t = Math.min(1, this.age[i] / this.life[i]);
      const dr = Math.max(0, 1 - s.drag * dt);
      this.vel[i * 3] *= dr;
      this.vel[i * 3 + 2] *= dr;
      this.vel[i * 3 + 1] = this.vel[i * 3 + 1] * dr - s.gravity * dt;
      this.pos[i * 3] += this.vel[i * 3] * dt;
      this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt;
      this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
      this.size[i] = Math.max(0, this.base[i] * (1 + s.grow * t));
      this.col[i * 4] = s.color[0] + (s.endColor[0] - s.color[0]) * t;
      this.col[i * 4 + 1] = s.color[1] + (s.endColor[1] - s.color[1]) * t;
      this.col[i * 4 + 2] = s.color[2] + (s.endColor[2] - s.color[2]) * t;
      this.col[i * 4 + 3] = s.alpha * (t < 0.1 ? t * 10 : 1 - (t - 0.1) / 0.9);
      if (this.age[i] >= this.life[i]) this.alive--;
    }
    const g = this.points.geometry;
    g.attributes.position.needsUpdate = true;
    (g.attributes.aColor as THREE.BufferAttribute).needsUpdate = true;
    (g.attributes.aSize as THREE.BufferAttribute).needsUpdate = true;
  }
}

const KIND_LIST: ParticleKind[] = ['smoke', 'dust', 'fire', 'spark', 'splash', 'flash', 'chips', 'blood', 'muzzle', 'heal', 'soot', 'flame', 'glow'];

/**
 * Pooled particle effects (no per-particle allocation). Density scales with the
 * graphics preset.
 */
export class ParticleSystem {
  readonly group = new THREE.Group();
  private normal: Pool;
  private additive: Pool;
  density = 1;

  constructor(capacity: number) {
    this.normal = new Pool(capacity, false);
    this.additive = new Pool(Math.max(256, capacity >> 1), true);
    this.group.add(this.normal.points, this.additive.points);
  }

  setPixelScale(h: number): void {
    this.normal.uniforms.uScale.value = h;
    this.additive.uniforms.uScale.value = h;
  }

  burst(kind: ParticleKind, x: number, y: number, z: number, count: number, speed = 1, up = 1): void {
    const n = Math.max(1, Math.round(count * this.density));
    const pool = SPECS[kind].additive ? this.additive : this.normal;
    const k = KIND_LIST.indexOf(kind);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * speed;
      pool.emit(kind, k, x + (Math.random() - 0.5) * 0.2, y, z + (Math.random() - 0.5) * 0.2, Math.cos(a) * r, (0.5 + Math.random()) * up * speed, Math.sin(a) * r);
    }
  }

  update(dt: number): void {
    this.normal.update(dt);
    this.additive.update(dt);
  }
}
